import React, { useEffect } from "react"
import { gql } from "@apollo/client"
import { productItemCardFragment } from "./ProductItemCard"
import { ProductItemCardFragment } from "./__generated__/ProductItemCardFragment"
import { queryHistogramResultFragment, Tag } from "./TagFilter"
import { take } from "lodash"
import {
  SearchQueryType,
  useSearchParameters,
  SearchOrder,
} from "../useSearchParameters"
import { getApolloClient } from "../apollo/ApolloClient"
import { EventType, trackEvent } from "../service/track"

const LIMIT = 25

const initialSearchQuery = gql`
  query InitialSearchQuery(
    $hist_args: query_histogram_args!
    $query_args: query_and_order_products_args!
  ) {
    query_histogram(args: $hist_args) {
      ...QueryHistogramResultFragment
    }
    query_and_order_products(args: $query_args, limit: 25) {
      ...ProductItemCardFragment
    }
  }

  ${queryHistogramResultFragment}
  ${productItemCardFragment}
`

const fetchMoreQuery = gql`
  query FetchMoreSearchQuery(
    $query_args: query_and_order_products_args!
    $offset: Int!
  ) {
    query_and_order_products(args: $query_args, offset: $offset, limit: 25) {
      ...ProductItemCardFragment
    }
  }

  ${productItemCardFragment}
`

interface QueryHistogramResult {
  type: Tag["type"]
  value: string
  value_count: number
}

interface InitialSearchQueryResults {
  query_and_order_products: ProductItemCardFragment[]
  query_histogram: QueryHistogramResult[]
}

interface FetchMoreSearchQueryResults {
  query_and_order_products: ProductItemCardFragment[]
}

export interface WithSearchWrappedProps {
  loadMore: () => void
  data: ProductItemCardFragment[]
  hasMore: boolean
  isLoading: boolean
  histogramResults: QueryHistogramResult[]
}

interface WithSearchOptions {
  staticSearchParams?: SearchQueryType
  primedData?: {
    primedProducts: ProductItemCardFragment[]
    primedHistogram: QueryHistogramResult[]
  }
}

export const withSearch = (
  WrappedComponent: React.ComponentType<WithSearchWrappedProps>,
  { staticSearchParams, primedData }: WithSearchOptions = {}
): React.FC<any> => props => {
  const [isFetchingData, setIsFetchingData] = React.useState(false)
  const [products, setProducts] = React.useState<ProductItemCardFragment[]>([])
  const [histogram, setHistogram] = React.useState<QueryHistogramResult[]>([])
  const [visibleOffset, setVisibleOffset] = React.useState(LIMIT)
  const [hasMore, setHasMore] = React.useState<boolean>(true)
  const [query] = useSearchParameters()
  const visibleProducts = take(products, visibleOffset)

  const getQueryParams = (queryObj?: SearchQueryType) => {
    const params = queryObj ?? { ...query, ...staticSearchParams }
    return {
      title: params.q?.length ? params.q : undefined,
      abvmin: params.abvmin,
      abvmax: params.abvmax,
      costmin: params.costmin,
      costmax: params.costmax,
      volumemin: params.volumemin,
      volumemax: params.volumemax,
      isonsale: params.isonsale,
      style: arrayLiteralStringFormat(params.style),
      region: arrayLiteralStringFormat(params.region),
      country: arrayLiteralStringFormat(params.country),
      distillery: arrayLiteralStringFormat(params.distillery),
      orderby: params.orderby,
    }
  }

  const hasUserDefinedFilters = () => {
    const entries = Object.entries(getQueryParams(query))
    return entries.filter(([_, v]) => v !== undefined && v !== null).length > 0
  }

  const fetchMore = async () => {
    if (isFetchingData) return
    if (products.length === 0) return

    setIsFetchingData(true)
    try {
      const { orderby, ...queryParams } = getQueryParams()

      const client = getApolloClient()

      const { data } = await client.query<FetchMoreSearchQueryResults>({
        query: fetchMoreQuery,
        variables: {
          hist_args: queryParams,
          query_args: {
            ...queryParams,
            orderby: orderby ?? SearchOrder.POPULARITY,
          },
          offset: products.length,
        },
      })

      if (data?.query_and_order_products) {
        const newProductsList = products.concat(data.query_and_order_products)
        setProducts(newProductsList)
        setHasMore(data.query_and_order_products.length === LIMIT)
      }
    } catch (error) {
      console.error(error)
    }
    setIsFetchingData(false)
  }

  useEffect(() => {
    const performInitalSearchQuery = async () => {
      setIsFetchingData(true)
      const { orderby, ...queryParams } = getQueryParams()

      try {
        const client = getApolloClient()
        const { data } = await client.query<InitialSearchQueryResults>({
          query: initialSearchQuery,
          variables: {
            hist_args: queryParams,
            query_args: {
              ...queryParams,
              orderby: orderby ?? SearchOrder.POPULARITY,
            },
            limit: LIMIT,
          },
        })
        setIsFetchingData(false)
        if (data?.query_and_order_products) {
          setProducts(data.query_and_order_products)
          setHistogram(data.query_histogram)
          setHasMore(data.query_and_order_products.length === LIMIT)
        }
      } catch (error) {
        console.error(error)
      }
      setIsFetchingData(false)
    }
    const hasUserFilters = hasUserDefinedFilters()
    setVisibleOffset(LIMIT)

    if (!hasUserFilters && primedData) {
      setProducts(primedData.primedProducts)
      setHistogram(primedData.primedHistogram)
    } else {
      performInitalSearchQuery().catch(error => console.error(error))
    }

    trackEvent(EventType.SEARCH, { ...query })
  }, [query])

  const onLoadMore = () => {
    const newOffset = visibleOffset + 10
    if (newOffset >= products.length) {
      fetchMore()

      if (visibleOffset < products.length) {
        setVisibleOffset(products.length)
      }
    } else {
      setVisibleOffset(newOffset)
    }
  }

  const canLoadMore = visibleProducts.length < products.length || hasMore

  return (
    <WrappedComponent
      data={visibleProducts}
      hasMore={canLoadMore}
      loadMore={onLoadMore}
      histogramResults={histogram}
      isLoading={isFetchingData}
      {...props}
    ></WrappedComponent>
  )
}

const arrayLiteralStringFormat = (values?: string[]): string | undefined =>
  values && values.length ? `{${values.join(",")}}` : undefined
